import React, { useState, useEffect } from 'react';
import assistantService from '../../services/assistantService';
import { useAuth } from '../../contexts/AuthContext';
import { useVapi } from '../../contexts/VoiceCallContext';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';

const { FiUser, FiMic, FiEdit, FiTrash2, FiPhone, FiPlus } = FiIcons;

const AssistantList = ({ onCreateNew, onEdit, onSelectAssistant }) => {
  const { user } = useAuth();
  const { startCall, isCallActive } = useVapi();
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadAssistants();
    } else if (!user) {
      setLoading(false);
    }
  }, [user]);

  const loadAssistants = async () => {
    try {
      if (!user?.id) {
        console.log('No user ID available');
        setAssistants([]);
        return;
      }
      
      console.log('Loading assistants for user:', user.id);
      const data = await assistantService.getUserAssistants(user.id);
      setAssistants(data);
    } catch (error) {
      toast.error('Failed to load assistants');
      console.error('Error loading assistants:', error);
      setAssistants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (assistant) => {
    if (!confirm(`Are you sure you want to delete "${assistant.name}"?`)) {
      return;
    }

    try {
      if (!user?.id) {
        toast.error('You must be logged in to delete assistants');
        return;
      }

      await assistantService.deleteAssistant(assistant.id, user.id);
      toast.success('Assistant deleted successfully');
      loadAssistants();
    } catch (error) {
      toast.error('Failed to delete assistant');
      console.error('Error deleting assistant:', error);
    }
  };

  const handleStartCall = async (assistant) => {
    if (isCallActive) {
      toast.error('End current call first');
      return;
    }

    console.log('Starting call with assistant:', assistant);
    
    try {
      // Pass the assistant object as the second parameter for transient assistant
      await startCall(null, assistant);
      
      toast.success(`Started call with ${assistant.name}`);
      if (onSelectAssistant) {
        onSelectAssistant(assistant);
      }
    } catch (error) {
      console.error('Error starting call:', error);
      toast.error(`Failed to start contact: ${error.message || 'Unknown error'}`);
    }
  };

  // Show login prompt if user is not authenticated
  if (!user) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiUser} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Please log in</h3>
        <p className="text-gray-600">You need to be logged in to view your assistants</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">My Assistants</h2>
        <motion.button
          onClick={onCreateNew}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          Create New
        </motion.button>
      </div>

      {assistants.length === 0 ? (
        <div className="text-center py-12">
          <SafeIcon icon={FiUser} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No assistants yet</h3>
          <p className="text-gray-600 mb-6">Create your first voice assistant to get started</p>
          <motion.button
            onClick={onCreateNew}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Assistant
          </motion.button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {assistants.map((assistant) => (
            <motion.div
              key={assistant.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{assistant.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <SafeIcon icon={FiMic} className="w-3 h-3" />
                      <span>{typeof assistant.voice === 'string' ? assistant.voice : assistant.voice?.voiceId || 'Default'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(assistant)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(assistant)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {assistant.first_message}
              </p>

              <div className="space-y-2 mb-4">
                <div className="text-xs text-gray-500">
                  Model: {assistant.model}
                </div>
                <div className="text-xs text-gray-500">
                  Created: {new Date(assistant.created_at).toLocaleDateString()}
                </div>
              </div>

              <motion.button
                onClick={() => handleStartCall(assistant)}
                disabled={isCallActive}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <SafeIcon icon={FiPhone} className="w-4 h-4" />
                Start Contact
              </motion.button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssistantList;